/**
 * Instanced batches of unit boxes (or cylinders): one draw call per batch, one
 * instance per part (lifted from `makeBatch` in the dungeon-floors prototype's
 * `dungeon-world.js`). A part keeps its base colour and its tile, so fog of war
 * recolours it, and an optional owner (a marker), so a picked-up item or a used
 * spring can be hidden and an item can bob. Each batch has its own copy of the
 * geometry with the per-instance attributes the Medium tier's surface and
 * cutaway programs read (`lwCutaway`, `dqLink`, `dqTop`); parts marked
 * `mediumOnly` go last and are left out of the draw at Off by instance count.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { presentation } from './constants.ts';

export type Part = {
  /** Tile index for fog of war; -1 when the part is always shown as seen. */
  readonly tile: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly sx: number;
  readonly sy: number;
  readonly sz: number;
  /** Rotation, applied in Y-X-Z order (the dressing kits' order; with rx 0 it is the plain Y then Z of earlier parts). */
  readonly rx: number;
  readonly ry: number;
  readonly rz: number;
  readonly color: THREE.Color;
  /** Marker owner (`item:3,4`), or null. */
  readonly owner: string | null;
  /** 1 when the leader's cutaway may open this part (timber frames and their bands). */
  readonly cutaway: number;
  /** A frame part's header: centre and signed half length (x when positive, z when negative); zeros for none. */
  readonly link: readonly [number, number, number, number];
  /** Drawn at the Medium tier only (the border rock). */
  readonly mediumOnly: boolean;
};

type Placed = Part & { hidden: boolean; dy: number };

const matrix = new THREE.Matrix4();
const position = new THREE.Vector3();
const scale = new THREE.Vector3();
const quaternion = new THREE.Quaternion();
const euler = new THREE.Euler();
const color = new THREE.Color();

/** A part with defaults for rotation and owner. */
export const part = (
  tile: number,
  x: number,
  y: number,
  z: number,
  size: readonly [number, number, number],
  tint: THREE.Color,
  extra: { rx?: number; ry?: number; rz?: number; owner?: string; cutaway?: number; link?: readonly [number, number, number, number]; mediumOnly?: boolean } = {},
): Part => ({
  tile,
  x,
  y,
  z,
  sx: size[0],
  sy: size[1],
  sz: size[2],
  rx: extra.rx ?? 0,
  ry: extra.ry ?? 0,
  rz: extra.rz ?? 0,
  color: tint,
  owner: extra.owner ?? null,
  cutaway: extra.cutaway ?? 0,
  link: extra.link ?? [0, 0, 0, 0],
  mediumOnly: extra.mediumOnly ?? false,
});

/** Bakes face shading into a `color` attribute from the normals: tops bright, sides darker (the Off tier is unlit). */
export const shadeGeometry = <G extends THREE.BufferGeometry>(geometry: G): G => {
  const normals = geometry.getAttribute('normal');
  const colors = new Float32Array(normals.count * 3);
  const { top, bottom, northSouth, eastWest } = presentation.shade;
  for (let i = 0; i < normals.count; i += 1) {
    const nx = Math.abs(normals.getX(i));
    const ny = normals.getY(i);
    const f = ny > 0.5 ? top : ny < -0.5 ? bottom : nx > 0.5 ? eastWest : northSouth;
    colors.set([f, f, f], i * 3);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geometry;
};

export class PartBatch {
  private readonly parts: Placed[] = [];
  private mesh: THREE.InstancedMesh | null = null;
  private geometry: THREE.BufferGeometry | null = null;

  constructor(private readonly name: string) {}

  get size(): number {
    return this.parts.length;
  }

  add(p: Part): void {
    this.parts.push({ ...p, hidden: false, dy: 0 });
  }

  /** The instanced mesh for the parts added so far, or null when there are none. */
  build(shared: THREE.BufferGeometry, material: THREE.Material): THREE.InstancedMesh | null {
    if (this.parts.length === 0) return null;
    // Medium-only parts last, so the Off tier draws a prefix of the instances.
    this.parts.sort((a, b) => Number(a.mediumOnly) - Number(b.mediumOnly));
    const geometry = shared.clone();
    geometry.setAttribute('lwCutaway', new THREE.InstancedBufferAttribute(Float32Array.from(this.parts, (p) => p.cutaway), 1));
    geometry.setAttribute('dqLink', new THREE.InstancedBufferAttribute(Float32Array.from(this.parts.flatMap((p) => p.link)), 4));
    geometry.setAttribute('dqTop', new THREE.Float32BufferAttribute(new Float32Array(geometry.getAttribute('position').count).fill(presentation.medium.surfaces.noTop), 1));
    this.geometry = geometry;
    const mesh = new THREE.InstancedMesh(geometry, material, this.parts.length);
    mesh.name = `dungeon-${this.name}`;
    // One batch covers the whole floor; per-instance culling is not worth its CPU cost here.
    mesh.frustumCulled = false;
    this.mesh = mesh;
    this.parts.forEach((_, i) => {
      this.place(i);
    });
    this.recolor(() => true, color);
    return mesh;
  }

  /** Instances drawn at a tier: every part at Medium, all but the medium-only ones at Off. */
  setTier(medium: boolean): void {
    if (this.mesh !== null) this.mesh.count = medium ? this.parts.length : this.parts.filter((p) => !p.mediumOnly).length;
  }

  /** Fog of war: parts on unseen tiles fade toward the fog colour and darken. */
  recolor(seen: (tile: number) => boolean, fog: THREE.Color, fade: number = presentation.fog.fade, darken: number = presentation.fog.darken): void {
    const mesh = this.mesh;
    if (mesh === null) return;
    this.parts.forEach((p, i) => {
      color.copy(p.color);
      if (p.tile >= 0 && !seen(p.tile)) color.lerp(fog, fade).multiplyScalar(darken);
      mesh.setColorAt(i, color);
    });
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  /** Hides (or shows again) every part of a marker. */
  setHidden(owner: string, hidden: boolean): void {
    this.forOwner(owner, (p) => {
      p.hidden = hidden;
    });
  }

  /** Lifts every part of a marker by `dy` (an item's bob). */
  setOffset(owner: string, dy: number): void {
    this.forOwner(owner, (p) => {
      p.dy = dy;
    });
  }

  has(owner: string): boolean {
    return this.parts.some((p) => p.owner === owner && !p.hidden);
  }

  dispose(): void {
    this.mesh?.removeFromParent();
    this.mesh?.dispose();
    this.geometry?.dispose();
    this.mesh = null;
    this.geometry = null;
  }

  private forOwner(owner: string, change: (p: Placed) => void): void {
    let touched = false;
    this.parts.forEach((p, i) => {
      if (p.owner !== owner) return;
      change(p);
      this.place(i);
      touched = true;
    });
    if (touched && this.mesh) this.mesh.instanceMatrix.needsUpdate = true;
  }

  private place(i: number): void {
    const p = this.parts[i];
    if (p === undefined || this.mesh === null) return;
    position.set(p.x, p.y + p.dy, p.z);
    // Hiding is a zero scale: the instance stays in the batch, so indices never shift.
    if (p.hidden) scale.set(0, 0, 0);
    else scale.set(p.sx, p.sy, p.sz);
    quaternion.setFromEuler(euler.set(p.rx, p.ry, p.rz, 'YXZ'));
    this.mesh.setMatrixAt(i, matrix.compose(position, quaternion, scale));
  }
}

/** What `mergeStatic` made: the merged meshes it added (each one draw) and the meshes they replaced. */
export type MergeStats = { readonly merged: number; readonly replaced: number };

/**
 * Static batching of a built scene (slice 6a; lifted from lantern-water's
 * `world.js` `batchWorld`): after every prefab is placed, the contact
 * shadows the helpers queued become one transparent mesh, and every opaque,
 * non-shader mesh is baked into world space and merged with the others of its
 * material and shadow flags into one mesh (one draw). Geometries merge only
 * with the same attribute set, so each material's meshes merge per attribute
 * signature. A per-vertex `lwCutaway` flag (1 under an object marked
 * `userData.cutaway`) survives the merge, so one shared material can open the
 * leader's cutaway through the eastern house only; `onCutaway` is called for
 * each material that carries a flagged vertex. Uses three's own
 * `BufferGeometryUtils.mergeGeometries` (`three/addons`).
 */
export const mergeStatic = (root: THREE.Object3D, contactMaterial: THREE.Material, onCutaway: (material: THREE.Material) => void): MergeStats => {
  root.updateMatrixWorld(true);
  const contacts: THREE.BufferGeometry[] = [];
  root.traverse((o) => {
    const list = o.userData['contacts'] as THREE.BufferGeometry[] | undefined;
    if (list === undefined) return;
    for (const g of list) {
      contacts.push(g.clone().applyMatrix4(o.matrixWorld));
      g.dispose();
    }
    delete o.userData['contacts'];
  });
  let merged = 0;
  if (contacts.length > 0) {
    const g = mergeGeometries(contacts);
    for (const c of contacts) c.dispose();
    if (g !== null) {
      const m = new THREE.Mesh(g, contactMaterial);
      m.name = 'contact-shadows';
      m.renderOrder = 1;
      root.add(m);
      merged += 1;
    }
  }
  type Bucket = { readonly material: THREE.Material; readonly cast: boolean; readonly receive: boolean; readonly geometries: THREE.BufferGeometry[]; cutaway: boolean };
  const buckets = new Map<string, Bucket>();
  const remove: THREE.Object3D[] = [];
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh) || o.userData['dynamic'] === true) return;
    const material: unknown = o.material;
    if (!(material instanceof THREE.Material) || material.transparent || material instanceof THREE.ShaderMaterial) return;
    const g = (o.geometry as THREE.BufferGeometry).clone().applyMatrix4(o.matrixWorld);
    const flat = g.index !== null ? g.toNonIndexed() : g;
    if (flat !== g) g.dispose();
    // A degenerate triangle's vertex has a zero normal; the lit shader's normalize() of it is NaN on some GPUs, and
    // bloom spreads one NaN pixel over a whole mip. Such a vertex faces up instead.
    const normal = flat.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (normal !== undefined) {
      for (let i = 0; i < normal.count; i += 1) if (normal.getX(i) === 0 && normal.getY(i) === 0 && normal.getZ(i) === 0) normal.setXYZ(i, 0, 1, 0);
    }
    const flag = o.userData['cutaway'] === true ? 1 : 0;
    flat.setAttribute('lwCutaway', new THREE.Float32BufferAttribute(new Float32Array(flat.getAttribute('position').count).fill(flag), 1));
    const signature = Object.keys(flat.attributes).sort().join(',');
    const key = `${material.uuid}_${o.castShadow}_${o.receiveShadow}_${signature}`;
    let b = buckets.get(key);
    if (b === undefined) {
      b = { material, cast: o.castShadow, receive: o.receiveShadow, geometries: [], cutaway: false };
      buckets.set(key, b);
    }
    b.cutaway ||= flag === 1;
    b.geometries.push(flat);
    remove.push(o);
  });
  for (const o of remove) o.removeFromParent();
  for (const b of buckets.values()) {
    const g = mergeGeometries(b.geometries, false);
    for (const x of b.geometries) x.dispose();
    if (g === null) throw new Error('static geometry merge failed');
    if (b.cutaway) onCutaway(b.material);
    const m = new THREE.Mesh(g, b.material);
    m.castShadow = b.cast;
    m.receiveShadow = b.receive;
    m.name = `merged:${b.material.name || b.material.type}`;
    root.add(m);
    merged += 1;
  }
  return { merged, replaced: remove.length };
};
